/*
  The Halloween stage's music and story on the Halloween product page. Music
  waits for the Sound button, because a tap anywhere else on this page only
  ever means buying; the button shows what is playing and turns it off.
  Thunder belongs to the storm's own script.
  The story plays only from its own button. While it plays, the stage
  carries the current beat (data-beat, from each script line's start time),
  the CSS poses the cast for that beat, and the line being spoken shows in
  its speaker's speech bubble. One source plays at a time: starting the
  story stops the music, and starting the music stops the story.
*/
(() => {
  document.querySelectorAll('[data-halloween-lab]').forEach((lab) => {
    const button = lab.querySelector('[data-sound]');
    const label = button && button.querySelector('[data-sound-label]');
    const audio = lab.querySelector('.lab__audio');
    const story = lab.querySelector('[data-story]');
    const storyAudio = lab.querySelector('.lab__story-audio');

    if (story && storyAudio) {
      const storyLabel = story.querySelector('[data-story-label]');
      const lines = Array.from(lab.querySelectorAll('.lab__line'));
      const bubbles = Array.from(lab.querySelectorAll('[data-say]'));
      const stage = lab.querySelector('.lab__cast');
      let beat = '';
      let said = null;
      let frame = 0;

      // Poses the cast for the current beat and fills the speaker's bubble; clears both while paused or over.
      const follow = () => {
        const time = storyAudio.currentTime;
        const playing = time > 0 && !storyAudio.paused && !storyAudio.ended;
        let current = null;
        if (playing) {
          lines.forEach((line) => {
            if (Number(line.dataset.start) <= time) current = line;
          });
        }
        let next = '';
        if (playing) next = current ? current.dataset.beat : '00';
        if (next !== beat) {
          beat = next;
          lab.classList.toggle('lab--story', Boolean(beat));
          if (beat) lab.dataset.beat = beat;
          else delete lab.dataset.beat;
        }
        const speaking = current && time < Number(current.dataset.end) ? current : null;
        if (speaking === said) return;
        said = speaking;
        bubbles.forEach((bubble) => {
          const mine = Boolean(speaking) && bubble.dataset.say.split(' ').includes(speaking.dataset.who);
          bubble.textContent = mine ? speaking.querySelector('.lab__line-say').textContent : '';
          bubble.hidden = !mine;
        });
      };

      // timeupdate fires only a few times a second, so a frame loop keeps the beats on the sound while it plays.
      const tick = () => {
        follow();
        frame = storyAudio.paused || storyAudio.ended ? 0 : requestAnimationFrame(tick);
      };

      // Brings the cast into view without scrolling the story button off the top.
      const showStage = () => {
        if (!stage) return;
        const lift = Math.min(
          stage.getBoundingClientRect().bottom + 16 - window.innerHeight,
          story.getBoundingClientRect().top - 80
        );
        if (lift <= 0) return;
        const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollBy({ top: lift, behavior: still ? 'auto' : 'smooth' });
      };

      const storyUnavailable = () => {
        story.setAttribute('aria-pressed', 'false');
        story.disabled = true;
        if (storyLabel) storyLabel.textContent = story.dataset.unavailable;
      };

      story.hidden = false;

      story.addEventListener('click', () => {
        if (!storyAudio.paused) {
          storyAudio.pause();
          return;
        }
        const playing = storyAudio.play();
        if (playing && playing.catch) {
          // A pause before playback starts rejects with AbortError; that is not a failure.
          playing.catch((error) => {
            if (error.name !== 'AbortError') storyUnavailable();
          });
        }
      });

      storyAudio.addEventListener('play', () => {
        story.setAttribute('aria-pressed', 'true');
        cancelAnimationFrame(frame);
        tick();
        showStage();
      });
      storyAudio.addEventListener('pause', () => {
        story.setAttribute('aria-pressed', 'false');
        follow();
      });
      storyAudio.addEventListener('ended', () => {
        storyAudio.currentTime = 0;
        follow();
      });
      storyAudio.addEventListener('timeupdate', follow);
      storyAudio.addEventListener('error', storyUnavailable);
      if (audio) audio.addEventListener('play', () => storyAudio.pause());
    }

    if (!button || !audio) return;

    // on: music is playing. wanted: it should be, as soon as the browser allows it.
    let on = false;
    let wanted = false;

    const setOn = (value) => {
      on = value;
      button.setAttribute('aria-pressed', String(value));
      lab.classList.toggle('lab--loud', value);
    };

    const unavailable = () => {
      setOn(false);
      button.disabled = true;
      if (label) label.textContent = button.dataset.unavailable;
    };

    const begin = () => {
      if (!wanted) {
        audio.pause();
        return;
      }
      setOn(true);
    };

    const refused = (error) => {
      // A pause before playback starts rejects with AbortError; that is not a failure.
      if (error.name === 'AbortError') return;
      // Browsers can hold sound even after a tap; the Sound button tries again.
      if (error.name === 'NotAllowedError') {
        setOn(false);
        return;
      }
      unavailable();
    };

    const start = () => {
      const playing = audio.play();
      if (playing && playing.then) playing.then(begin, refused);
      else begin();
    };

    button.hidden = false;

    button.addEventListener('click', () => {
      if (on) {
        wanted = false;
        audio.pause();
        setOn(false);
        return;
      }
      wanted = true;
      setOn(true);
      start();
    });

    // The story took over: the music stays off until the Sound button asks again.
    const hush = () => {
      wanted = false;
      if (!on) return;
      audio.pause();
      setOn(false);
    };
    if (storyAudio) storyAudio.addEventListener('play', hush);

    document.addEventListener('visibilitychange', () => {
      if (!on) return;
      if (document.hidden) {
        audio.pause();
      } else {
        const playing = audio.play();
        if (playing && playing.catch) playing.catch(() => setOn(false));
      }
    });
  });
})();
